import { API_PREFIX, paginatedFromDto, type HttpClient } from "@nasam/kernel";
import type {
  DriverAssignmentHistoryQuery,
  DriverAssignmentHistorySortBy,
  DriverAssignmentSortBy,
  DriverAssignmentsListQuery,
  DriverAssignmentsRepository,
} from "../domain";
import {
  assignableAssetsResponseDtoSchema,
  assignableDriversResponseDtoSchema,
  branchOptionsResponseDtoSchema,
  driverAssignmentCreateDtoSchema,
  driverAssignmentDeleteResponseDtoSchema,
  driverAssignmentDetailResponseDtoSchema,
  driverAssignmentHistoryResponseDtoSchema,
  driverAssignmentListResponseDtoSchema,
  driverAssignmentResponseDtoSchema,
} from "./driverAssignmentDtos";
import {
  assetFromDto,
  driverAssignmentDetailsFromDto,
  driverAssignmentFromDto,
  driverAssignmentHistoryItemFromDto,
  driverFromDto,
} from "./driverAssignmentMappers";

const DRIVER_ASSIGNMENTS_PATH = `${API_PREFIX}/driver-assignments`;
const BRANCHES_PATH = `${API_PREFIX}/branches`;
// The Branch filter is a plain (non-paged) picker; one page of the largest
// size covers an account's branches.
const BRANCH_PAGE_LIMIT = 100;

// The screen sorts the name columns per UI language (driverNameEn /
// driverNameAr ...); the API takes one name per column.
const SORT_BY_TO_API: Record<DriverAssignmentSortBy, string> = {
  driverNameEn: "driverName",
  driverNameAr: "driverName",
  assetNameEn: "assetName",
  assetNameAr: "assetName",
  assignedAt: "assignedAt",
};

function listParams(query: DriverAssignmentsListQuery) {
  const search = query.search?.trim();
  return {
    page: query.page,
    limit: query.limit,
    sort: query.sort,
    sortBy: query.sortBy === undefined ? undefined : SORT_BY_TO_API[query.sortBy],
    search: search === "" ? undefined : search,
    fromAssignedAt: query.fromAssignedAt,
    toAssignedAt: query.toAssignedAt,
  };
}

const HISTORY_SORT_BY_TO_API: Record<DriverAssignmentHistorySortBy, string> = {
  ...SORT_BY_TO_API,
  actionAt: "actionAt",
  action: "action",
};

function historyParams(query: DriverAssignmentHistoryQuery) {
  const search = query.search?.trim();
  return {
    page: query.page,
    limit: query.limit,
    sort: query.sort,
    sortBy:
      query.sortBy === undefined
        ? undefined
        : HISTORY_SORT_BY_TO_API[query.sortBy],
    search: search === "" ? undefined : search,
    action: query.action,
    fromAssignedAt: query.fromAssignedAt,
    toAssignedAt: query.toAssignedAt,
    fromActionAt: query.fromActionAt,
    toActionAt: query.toActionAt,
  };
}

/** Every driver-assignment endpoint (NASAM-283 Create, 284 List, 779
 *  Details, 285 Update, 286 Delete, 287 History); each still has a mock
 *  switch in fleetMonitoringFeature.ts. */
export type DriverAssignmentsHttpRepository = Pick<
  DriverAssignmentsRepository,
  | "list"
  | "listHistory"
  | "getById"
  | "create"
  | "update"
  | "delete"
  | "listAvailableDrivers"
  | "listAssignableAssets"
  | "listBranches"
>;

export function createDriverAssignmentsHttpRepository(
  http: HttpClient,
): DriverAssignmentsHttpRepository {
  return {
    // Admin: every active assignment in the account; supervisor: only those
    // on their assets. Defaults (page 1, limit 10, newest first) are the
    // API's own too.
    async list(query = {}) {
      const response = await http.get(DRIVER_ASSIGNMENTS_PATH, {
        params: listParams(query),
      });
      return paginatedFromDto(
        driverAssignmentListResponseDtoSchema.parse(response),
        driverAssignmentFromDto,
      );
    },

    // Read-only. Admin: the account's history; supervisor: only the history
    // of the assets currently assigned to them. Defaults (actionAt desc) are
    // the API's own too.
    async listHistory(query = {}) {
      const response = await http.get(`${DRIVER_ASSIGNMENTS_PATH}/history`, {
        params: historyParams(query),
      });
      return paginatedFromDto(
        driverAssignmentHistoryResponseDtoSchema.parse(response),
        driverAssignmentHistoryItemFromDto,
      );
    },

    // Scoped like the list. An ended, deleted or out-of-scope assignment is a
    // 404 and a load failure a 500 (never partial data); both propagate as
    // HttpError, which the screen turns into its "no longer available" /
    // "try again" states.
    async getById(id) {
      const response = await http.get(
        `${DRIVER_ASSIGNMENTS_PATH}/${encodeURIComponent(id)}`,
      );
      return driverAssignmentDetailsFromDto(
        driverAssignmentDetailResponseDtoSchema.parse(response).data,
      );
    },

    // Already scoped server-side: active drivers with no active asset; a
    // supervisor only sees drivers of the accounts owning their assets.
    async listAvailableDrivers() {
      const response = await http.get(`${DRIVER_ASSIGNMENTS_PATH}/drivers`);
      return assignableDriversResponseDtoSchema
        .parse(response)
        .data.map(driverFromDto);
    },

    // Admin: every asset of the account; supervisor: only their own. Assets
    // that already have an active driver are left out server-side.
    async listAssignableAssets(query = {}) {
      const response = await http.get(`${DRIVER_ASSIGNMENTS_PATH}/assets`, {
        params:
          query.branchId === undefined ? undefined : { branchId: query.branchId },
      });
      return assignableAssetsResponseDtoSchema
        .parse(response)
        .data.map(assetFromDto);
    },

    async listBranches() {
      const response = await http.get(BRANCHES_PATH, {
        params: { page: 1, limit: BRANCH_PAGE_LIMIT },
      });
      return branchOptionsResponseDtoSchema
        .parse(response)
        .data.filter((branch) => branch.isActive !== false)
        .map(({ id, nameEn, nameAr }) => ({ id, nameEn, nameAr }));
    },

    // One transaction server-side (assignment + history row + audit record).
    // 400 / 404 / 409 propagate as HttpError carrying the backend's
    // translated message, which the form shows in the error toast.
    async create(input) {
      const response = await http.post(
        DRIVER_ASSIGNMENTS_PATH,
        driverAssignmentCreateDtoSchema.parse(input),
      );
      const parsed = driverAssignmentResponseDtoSchema.parse(response);
      return {
        assignment: driverAssignmentFromDto(parsed.data),
        message: parsed.message ?? null,
      };
    },

    // Ends the active assignment: the driver is free again and the asset has
    // no driver (Tracking stops showing one); a "Removed" history row and an
    // audit entry are added. A 404 (already ended / out of scope) propagates.
    async delete(id) {
      const response = await http.delete(
        `${DRIVER_ASSIGNMENTS_PATH}/${encodeURIComponent(id)}`,
      );
      return {
        message:
          driverAssignmentDeleteResponseDtoSchema.parse(response ?? {}).message ??
          null,
      };
    },

    // Closes the current assignment and opens a new one for the pair (two
    // "Updated" history rows + an audit entry, one transaction) and returns
    // the new active assignment. An unchanged pair returns the current one
    // as-is -- the form never sends it, since Save stays disabled until
    // something changes. 404 / 409 propagate like create's.
    async update(id, input) {
      const response = await http.patch(
        `${DRIVER_ASSIGNMENTS_PATH}/${encodeURIComponent(id)}`,
        driverAssignmentCreateDtoSchema.parse(input),
      );
      const parsed = driverAssignmentResponseDtoSchema.parse(response);
      return {
        assignment: driverAssignmentFromDto(parsed.data),
        message: parsed.message ?? null,
      };
    },
  };
}
